import { Component } from '@theme/component';

/**
 * Topbar component that allows fading between USP content on mobile.
 * Based on the AnnouncementBar component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class Topbar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Media query for mobile devices.
   * @type {MediaQueryList|undefined}
   */
  #mobileQuery = undefined;

  connectedCallback() {
    super.connectedCallback();

    // Only enable autoplay on mobile
    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileQuery.addEventListener('change', this.#handleMediaChange);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize: show all slides on desktop, first slide on mobile
    if (this.#mobileQuery.matches) {
      // On mobile, show only first slide and start autoplay
      this.current = 0;
      this.play();
    } else {
      // On desktop, show all slides
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#mobileQuery) {
      this.#mobileQuery.removeEventListener('change', this.#handleMediaChange);
    }

    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback (only on mobile).
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    if (!this.#mobileQuery?.matches) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      if (!this.#mobileQuery?.matches) {
        this.suspend();
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled and on mobile.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    if (!this.#mobileQuery?.matches) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    // Only hide/show slides on mobile
    if (!this.#mobileQuery?.matches) {
      // On desktop, show all slides
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
      return;
    }

    // On mobile, show only one slide at a time
    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Handle media query changes (mobile/desktop switch).
   */
  #handleMediaChange = () => {
    if (this.#mobileQuery?.matches) {
      // On mobile, show first slide and start autoplay
      this.current = 0;
      this.resume();
    } else {
      // On desktop, show all slides
      this.suspend();
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
    }
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('topbar-component')) {
  customElements.define('topbar-component', Topbar);
}

